// src/pages/analytics/AiOrdersTab.tsx
//
// AI Orders review queue. Orders captured by the AI intake channels
// (voice call transcription or inbound email parsing) land here as
// `pending_review`; a buyer verifies the extracted lines + pricing,
// assigns a customer when the AI couldn't match one, then approves
// (→ eligible for posting to Advantage) or rejects with a reason.
//
// Queue view: status + source filter pills, table of orders, 60s
// auto-refresh while the Pending filter is active. Row click opens a
// detail drawer (same pattern as PurchasingPipelineTab's ContainerDrawer,
// including the mousedown-origin guard from feedback_modal_drag_select_bug).
//
// Line edits are LOCAL state; "Save changes" PATCHes the FULL lines array
// and the server recomputes ext/subtotal/tax/total.

import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Bot, RefreshCw, AlertTriangle, X, Trash2, Loader2, Save,
  Phone, Mail, Check, XCircle, ChevronDown, ChevronRight,
} from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import { fmtLocalTime } from '../../lib/formatTime';
import type {
  AiOrderSummary, AiOrderDetail, AiOrderLine, AiOrderStatus, AiOrderSource,
} from '../../types';

// ── Shared formatting / badges ──────────────────────────────────────

const STATUS_LABELS: Record<AiOrderStatus, { label: string; cls: string }> = {
  pending_review: { label: 'Pending review', cls: 'bg-blue-500/15 text-blue-200 border-blue-500/30' },
  approved:       { label: 'Approved',       cls: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30' },
  rejected:       { label: 'Rejected',       cls: 'bg-red-500/15 text-red-200 border-red-500/30' },
  posted:         { label: 'Posted',         cls: 'bg-slate-700/50 text-slate-300 border-slate-600/50' },
};

const fmtCurrency = (n: number | null | undefined): string =>
  n == null
    ? '—'
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

// Requested date is a calendar date (no time-of-day meaning) — render the
// raw YYYY-MM-DD rather than fmtLocalTime, which would shift the day for
// users west of UTC.
function fmtDateOnly(iso: string | null): string {
  if (!iso) return '—';
  return iso.length >= 10 ? iso.slice(0, 10) : iso;
}

/** Extract the server's error message (409 conflicts, 503 admin_busy, …). */
function errMsg(e: unknown): string {
  const anyE = e as { response?: { data?: { error?: string; reason?: string } }; message?: string };
  if (anyE?.response?.data?.reason === 'admin_busy') {
    return 'The agent is busy with another admin write — try again in a moment.';
  }
  return anyE?.response?.data?.error || anyE?.message || 'Request failed';
}

function SourceCell({ source }: { source: AiOrderSource }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-300">
      {source === 'voice' ? <Phone size={13} className="text-blue-300" /> : <Mail size={13} className="text-fuchsia-300" />}
      {source === 'voice' ? 'Voice' : 'Email'}
    </span>
  );
}

function StatusBadge({ status }: { status: AiOrderStatus }) {
  const sty = STATUS_LABELS[status] ?? STATUS_LABELS.pending_review;
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[11px] border ${sty.cls}`}>
      {sty.label}
    </span>
  );
}

function ConfidenceBadge({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const cls =
    pct >= 90 ? 'bg-emerald-500/15 text-emerald-200 border-emerald-500/30'
    : pct >= 60 ? 'bg-amber-500/15 text-amber-200 border-amber-500/30'
    : 'bg-red-500/15 text-red-200 border-red-500/30';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[11px] border tabular-nums ${cls}`} title="Extraction confidence">
      {pct}%
    </span>
  );
}

function ConfidenceDot({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  const cls = pct >= 90 ? 'bg-emerald-400' : pct >= 60 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${cls}`}
      title={`${pct}% match confidence`}
    />
  );
}

function PriceSourceTag({ source }: { source: string }) {
  const cls =
    source === 'special' ? 'text-fuchsia-300 border-fuchsia-500/30 bg-fuchsia-500/10'
    : source === 'base' ? 'text-slate-400 border-slate-600/50 bg-slate-700/30'
    : 'text-blue-300 border-blue-500/30 bg-blue-500/10'; // tier{n}
  return (
    <span className={`inline-block px-1.5 py-px rounded text-[10px] border ${cls}`} title="Price source">
      {source}
    </span>
  );
}

// ── Queue view ──────────────────────────────────────────────────────

type StatusFilter = AiOrderStatus | '';
type SourceFilter = AiOrderSource | '';

const STATUS_PILLS: { key: StatusFilter; label: string }[] = [
  { key: 'pending_review', label: 'Pending review' },
  { key: 'approved',       label: 'Approved' },
  { key: 'rejected',       label: 'Rejected' },
  { key: '',               label: 'All' },
];

const SOURCE_PILLS: { key: SourceFilter; label: string }[] = [
  { key: '',      label: 'All sources' },
  { key: 'voice', label: 'Voice' },
  { key: 'email', label: 'Email' },
];

export function AiOrdersTab() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending_review');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['ai-orders', 'list', statusFilter, sourceFilter],
    queryFn: () => api.getAiOrders(statusFilter || undefined, sourceFilter || undefined),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    // The pending queue is the live triage view — keep it fresh. React Query
    // clears the interval when the component unmounts or the filter changes.
    refetchInterval: statusFilter === 'pending_review' ? 60_000 : false,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Bot size={20} /> AI Orders
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Orders captured by AI from voice calls and email. Review the extracted lines, then approve or reject.
          </p>
        </div>
        <button
          onClick={() => listQ.refetch()}
          className="p-2 rounded-md bg-slate-800/60 border border-slate-700 text-slate-300 hover:bg-slate-700/60"
          title="Refresh"
        >
          <RefreshCw size={14} className={listQ.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap items-center gap-4">
        <FilterPillGroup
          pills={STATUS_PILLS}
          active={statusFilter}
          onChange={(k) => setStatusFilter(k)}
        />
        <div className="h-5 w-px bg-slate-700/60" />
        <FilterPillGroup
          pills={SOURCE_PILLS}
          active={sourceFilter}
          onChange={(k) => setSourceFilter(k)}
        />
      </div>

      {listQ.isLoading && (
        <div className="p-8 text-center text-slate-400 text-sm">Loading AI orders…</div>
      )}

      {listQ.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load AI orders</div>
            <div className="text-xs text-red-300/70 mt-0.5">{errMsg(listQ.error)}</div>
          </div>
        </div>
      )}

      {!listQ.isLoading && !listQ.error && (
        <OrderTable rows={listQ.data ?? []} onSelect={(id) => setSelectedId(id)} />
      )}

      {selectedId && (
        <OrderDrawer
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.refetch()}
        />
      )}
    </div>
  );
}

function FilterPillGroup<K extends string>({
  pills, active, onChange,
}: {
  pills: { key: K; label: string }[];
  active: K;
  onChange: (k: K) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {pills.map((p) => (
        <button
          key={p.key || 'all'}
          onClick={() => onChange(p.key)}
          className={`px-3 py-2 md:py-1 rounded-full text-xs border transition-all ${
            active === p.key
              ? 'bg-blue-500/20 border-blue-500/40 text-blue-100'
              : 'bg-slate-800/40 border-slate-700/50 text-slate-400 hover:text-slate-200 hover:bg-slate-800/70'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ── List table ──────────────────────────────────────────────────────

function OrderTable({ rows, onSelect }: { rows: AiOrderSummary[]; onSelect: (id: string) => void }) {
  if (rows.length === 0) {
    return (
      <div className="p-8 text-center text-slate-500 text-sm border border-slate-800 rounded-lg">
        No orders match the current filters. New voice and email orders land here automatically.
      </div>
    );
  }
  return (
    <>
    {/* Mobile: stacked cards (the table is unusable at phone widths). */}
    <div className="md:hidden space-y-2">
      {rows.map((o) => (
        <button
          key={o.id}
          onClick={() => onSelect(o.id)}
          className="w-full text-left p-3 rounded-lg border border-slate-800 bg-slate-900/40 hover:bg-slate-800/40 transition-colors space-y-1.5"
        >
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="min-w-0">
              <div className="text-slate-100 font-medium text-sm truncate">{o.customerName || '—'}</div>
              <div className="text-[11px]">
                {o.customerCode != null ? (
                  <span className="text-slate-500">#{o.customerCode}</span>
                ) : (
                  <span className="text-amber-300 font-medium">Unassigned</span>
                )}
              </div>
            </div>
            <span className="shrink-0 text-xs"><SourceCell source={o.source} /></span>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            <span className="text-slate-100 tabular-nums font-medium">{fmtCurrency(o.total)}</span>
            <span className="text-slate-500 text-[11px]">· {o.lineCount} line{o.lineCount === 1 ? '' : 's'}</span>
            <ConfidenceBadge value={o.confidence} />
            {o.needsClarification && (
              <span className="inline-block px-2 py-0.5 rounded text-[11px] border bg-amber-500/15 text-amber-200 border-amber-500/30">
                Needs review
              </span>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 text-[11px] text-slate-400">
            <span>{fmtLocalTime(o.createdAt)}</span>
            <StatusBadge status={o.status} />
          </div>
        </button>
      ))}
    </div>

    {/* Desktop: the original table, unchanged. */}
    <div className="hidden md:block overflow-x-auto border border-slate-800 rounded-lg">
      <table className="w-full text-sm">
        <thead className="bg-slate-900/60 text-slate-400 text-xs uppercase tracking-wider">
          <tr>
            <th className="px-3 py-2 text-left">Source</th>
            <th className="px-3 py-2 text-left">Customer</th>
            <th className="px-3 py-2 text-right">Lines</th>
            <th className="px-3 py-2 text-right">Total</th>
            <th className="px-3 py-2 text-left">Confidence</th>
            <th className="px-3 py-2 text-left">Created</th>
            <th className="px-3 py-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rows.map((o) => (
            <tr
              key={o.id}
              onClick={() => onSelect(o.id)}
              className="hover:bg-slate-800/30 transition-colors cursor-pointer"
            >
              <td className="px-3 py-2"><SourceCell source={o.source} /></td>
              <td className="px-3 py-2">
                <div className="text-slate-100 font-medium">{o.customerName || '—'}</div>
                <div className="text-[11px]">
                  {o.customerCode != null ? (
                    <span className="text-slate-500">#{o.customerCode}</span>
                  ) : (
                    <span className="text-amber-300 font-medium">Unassigned</span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{o.lineCount}</td>
              <td className="px-3 py-2 text-right text-slate-100 tabular-nums">{fmtCurrency(o.total)}</td>
              <td className="px-3 py-2">
                <div className="flex items-center gap-1.5">
                  <ConfidenceBadge value={o.confidence} />
                  {o.needsClarification && (
                    <span className="inline-block px-2 py-0.5 rounded text-[11px] border bg-amber-500/15 text-amber-200 border-amber-500/30">
                      Needs review
                    </span>
                  )}
                </div>
              </td>
              <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{fmtLocalTime(o.createdAt)}</td>
              <td className="px-3 py-2"><StatusBadge status={o.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
}

// ── Detail drawer ───────────────────────────────────────────────────

function OrderDrawer({
  id, onClose, onChanged,
}: {
  id: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const detailKey = ['ai-orders', 'detail', id];
  const detailQ = useQuery({
    queryKey: detailKey,
    queryFn: () => api.getAiOrder(id),
    refetchOnWindowFocus: false,
  });

  // Track mousedown origin so a drag-select that ends outside the panel
  // doesn't close the drawer (see feedback_modal_drag_select_bug).
  const backdropMouseDown = useRef(false);

  // Push the fresh detail into the cache + refetch, and refresh the list.
  function refreshAll(detail?: AiOrderDetail) {
    if (detail) qc.setQueryData(detailKey, detail);
    qc.invalidateQueries({ queryKey: detailKey });
    onChanged();
  }

  const detail = detailQ.data;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex justify-end"
      onMouseDown={(e) => { backdropMouseDown.current = e.target === e.currentTarget; }}
      onClick={(e) => {
        if (e.target === e.currentTarget && backdropMouseDown.current) onClose();
        backdropMouseDown.current = false;
      }}
    >
      <div className="bg-slate-900 border-l border-slate-700 w-full max-w-3xl h-full overflow-y-auto shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between sticky top-0 bg-slate-900 z-10">
          <div className="flex items-center gap-2 min-w-0">
            <Bot size={18} className="text-blue-300 shrink-0" />
            <h3 className="text-lg font-semibold text-white truncate">
              {detail ? (detail.customerName || 'AI Order') : 'AI Order'}
            </h3>
            {detail && <StatusBadge status={detail.status} />}
            {detail?.needsClarification && (
              <span className="inline-block px-2 py-0.5 rounded text-[11px] border bg-amber-500/15 text-amber-200 border-amber-500/30">
                Needs review
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>

        {detailQ.isLoading && (
          <div className="p-8 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading order…
          </div>
        )}
        {detailQ.error && (
          <div className="m-5 p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm">
            <div className="font-medium flex items-center gap-2"><AlertTriangle size={14} /> Couldn't load order</div>
            <div className="text-xs text-red-300/70 mt-1">{errMsg(detailQ.error)}</div>
          </div>
        )}

        {detail && <OrderDetailBody detail={detail} onSaved={refreshAll} />}
      </div>
    </div>
  );
}

// ── Detail body (header + editable lines + transcript + actions) ────

function OrderDetailBody({
  detail, onSaved,
}: {
  detail: AiOrderDetail;
  onSaved: (d: AiOrderDetail) => void;
}) {
  const isPending = detail.status === 'pending_review';

  // Local edit state — re-synced whenever the server detail changes
  // (after save/approve/reject the fresh detail replaces the draft).
  const [lines, setLines] = useState<AiOrderLine[]>(detail.lines);
  const [customerCode, setCustomerCode] = useState(
    detail.customerCode == null ? '' : String(detail.customerCode),
  );
  const [dirty, setDirty] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [approveNote, setApproveNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    setLines(detail.lines);
    setCustomerCode(detail.customerCode == null ? '' : String(detail.customerCode));
    setDirty(false);
  }, [detail]);

  const saveM = useMutation({
    mutationFn: () => {
      const codeChanged =
        customerCode !== (detail.customerCode == null ? '' : String(detail.customerCode));
      return api.patchAiOrder(detail.id, {
        lines,
        ...(codeChanged
          ? { customerCode: customerCode.trim() === '' ? null : Number(customerCode) }
          : {}),
      });
    },
    onSuccess: (d) => { setErr(null); onSaved(d); },
    onError: (e) => setErr(errMsg(e)),
  });

  const approveM = useMutation({
    mutationFn: () => api.approveAiOrder(detail.id, approveNote.trim() || undefined),
    onSuccess: (d) => { setErr(null); onSaved(d); },
    onError: (e) => setErr(errMsg(e)),
  });

  const rejectM = useMutation({
    mutationFn: () => api.rejectAiOrder(detail.id, rejectReason.trim()),
    onSuccess: (d) => { setErr(null); onSaved(d); },
    onError: (e) => setErr(errMsg(e)),
  });

  const busy = saveM.isPending || approveM.isPending || rejectM.isPending;

  function updateQty(lineNo: number, qty: number) {
    setLines((curr) => curr.map((l) => (l.lineNo === lineNo ? { ...l, quantity: qty } : l)));
    setDirty(true);
  }
  function removeLine(lineNo: number) {
    setLines((curr) => curr.filter((l) => l.lineNo !== lineNo));
    setDirty(true);
  }

  const approveDisabledReason =
    detail.customerCode == null ? 'Assign a customer (and save) before approving'
    : dirty ? 'Save or discard your line edits before approving'
    : null;

  return (
    <div className="p-5 space-y-6">
      {err && (
        <div className="text-sm text-red-300 bg-red-950/30 border border-red-900/40 rounded px-3 py-2 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> <span>{err}</span>
        </div>
      )}

      {/* Header details */}
      <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm">
        <div>
          <dt className="text-xs text-slate-500 mb-1">Customer</dt>
          <dd className="text-slate-200">
            <div>{detail.customerName || '—'}</div>
            {isPending ? (
              <input
                type="number"
                value={customerCode}
                onChange={(e) => { setCustomerCode(e.target.value); setDirty(true); }}
                placeholder="Customer #"
                className="mt-1 w-28 bg-slate-800/60 border border-slate-700 text-slate-100 text-xs rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
              />
            ) : detail.customerCode != null ? (
              <div className="text-[11px] text-slate-500">#{detail.customerCode}</div>
            ) : null}
            {detail.customerCode == null && (
              <div className="text-[11px] text-amber-300 mt-0.5">Unassigned</div>
            )}
          </dd>
        </div>
        <DetailItem label="Source"><SourceCell source={detail.source} /></DetailItem>
        <DetailItem label="Created">{fmtLocalTime(detail.createdAt)}</DetailItem>
        <DetailItem label="Confidence"><ConfidenceBadge value={detail.confidence} /></DetailItem>
        <DetailItem label="Requested date">{fmtDateOnly(detail.requestedDate)}</DetailItem>
        <DetailItem label="Customer PO">{detail.customerPo || '—'}</DetailItem>
      </dl>

      {detail.notes && (
        <div>
          <div className="text-xs text-slate-500 mb-1">Notes</div>
          <div className="text-sm text-slate-300 whitespace-pre-wrap">{detail.notes}</div>
        </div>
      )}

      {detail.reviewedAt && (
        <div className="text-xs text-slate-500">
          Reviewed {fmtLocalTime(detail.reviewedAt)}
          {detail.reviewedBy ? ` by ${detail.reviewedBy}` : ''}
          {detail.reviewNotes ? ` — ${detail.reviewNotes}` : ''}
        </div>
      )}

      {/* Lines */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-slate-300">
          Lines <span className="text-slate-500 font-normal">({lines.length})</span>
        </h4>
        <div className="border border-slate-800 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/60 text-slate-400 text-xs">
              <tr>
                <th className="px-2 py-1.5 text-left">Item #</th>
                <th className="px-2 py-1.5 text-left">Description</th>
                <th className="px-2 py-1.5 text-right">Qty</th>
                <th className="px-2 py-1.5 text-left">UM</th>
                <th className="px-2 py-1.5 text-right">Price</th>
                <th className="px-2 py-1.5 text-right">Ext</th>
                <th className="px-2 py-1.5 text-center" title="Match confidence">Conf</th>
                {isPending && <th />}
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 && (
                <tr><td colSpan={isPending ? 8 : 7} className="px-2 py-4 text-center text-slate-500 text-xs">
                  No lines. An order needs at least one line before it can be approved.
                </td></tr>
              )}
              {lines.map((l) => (
                <tr key={l.lineNo} className="border-t border-slate-800">
                  <td className="px-2 py-1.5 text-slate-200 whitespace-nowrap align-top">{l.itemNo}</td>
                  <td className="px-2 py-1.5 align-top">
                    <div className="text-slate-200 flex items-center gap-1.5">
                      {l.description}
                      <PriceSourceTag source={l.priceSource} />
                    </div>
                    {l.matchedQuery && (
                      <div className="text-[11px] text-slate-500 italic mt-0.5">"{l.matchedQuery}"</div>
                    )}
                    {l.notes && (
                      <div className="text-[11px] text-slate-500 mt-0.5">{l.notes}</div>
                    )}
                  </td>
                  <td className="px-1 py-1 text-right align-top w-20">
                    {isPending ? (
                      <input
                        type="number"
                        min={0}
                        value={String(l.quantity)}
                        onChange={(e) => updateQty(l.lineNo, Number(e.target.value) || 0)}
                        className="w-full bg-slate-900 border border-slate-700 text-slate-100 text-sm rounded px-1.5 py-1 text-right focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                      />
                    ) : (
                      <span className="text-slate-300 tabular-nums">{l.quantity}</span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-slate-400 align-top">{l.um}</td>
                  <td className="px-2 py-1.5 text-right text-slate-300 tabular-nums align-top">{fmtCurrency(l.unitPrice)}</td>
                  <td className="px-2 py-1.5 text-right text-slate-300 tabular-nums align-top">{fmtCurrency(l.quantity * l.unitPrice)}</td>
                  <td className="px-2 py-1.5 text-center align-top"><ConfidenceDot value={l.confidence} /></td>
                  {isPending && (
                    <td className="px-2 py-1.5 w-8 align-top">
                      <button
                        onClick={() => removeLine(l.lineNo)}
                        disabled={busy}
                        className="p-1 rounded text-slate-500 hover:text-red-400 hover:bg-red-950/30 disabled:opacity-50"
                        title="Remove line"
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals (server-computed; a pending save may change these) */}
        <div className="flex justify-end">
          <dl className="text-sm text-right space-y-0.5">
            <div className="flex gap-6 justify-end">
              <dt className="text-slate-500">Subtotal</dt>
              <dd className="text-slate-300 tabular-nums w-24">{fmtCurrency(detail.subtotal)}</dd>
            </div>
            <div className="flex gap-6 justify-end">
              <dt className="text-slate-500">Tax</dt>
              <dd className="text-slate-300 tabular-nums w-24">{fmtCurrency(detail.tax)}</dd>
            </div>
            <div className="flex gap-6 justify-end font-medium">
              <dt className="text-slate-400">Total</dt>
              <dd className="text-white tabular-nums w-24">{fmtCurrency(detail.total)}</dd>
            </div>
          </dl>
        </div>

        {isPending && dirty && (
          <div className="flex items-center justify-end gap-2">
            <span className="text-[11px] text-slate-500">
              Totals recompute on save.
            </span>
            <button
              onClick={() => {
                setLines(detail.lines);
                setCustomerCode(detail.customerCode == null ? '' : String(detail.customerCode));
                setDirty(false);
                setErr(null);
              }}
              disabled={busy}
              className="px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm disabled:opacity-50"
            >
              Discard
            </button>
            <button
              onClick={() => saveM.mutate()}
              disabled={busy}
              className="px-3 py-1.5 rounded-md bg-blue-500/20 border border-blue-500/40 text-blue-100 hover:bg-blue-500/30 text-sm disabled:opacity-50 flex items-center gap-1.5"
            >
              {saveM.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              {saveM.isPending ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}
      </div>

      {/* Transcript */}
      {detail.transcript && (
        <div className="space-y-2">
          <button
            onClick={() => setShowTranscript((v) => !v)}
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-300 hover:text-white"
          >
            {showTranscript ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            {detail.source === 'voice' ? 'Call transcript' : 'Original email'}
          </button>
          {showTranscript && (
            <pre className="font-mono text-xs text-slate-300 whitespace-pre-wrap bg-slate-950/60 border border-slate-800 rounded p-3 max-h-72 overflow-y-auto">
              {detail.transcript}
            </pre>
          )}
        </div>
      )}

      {/* Actions */}
      {isPending && (
        <div className="pt-4 border-t border-slate-800 space-y-3">
          <h4 className="text-sm font-semibold text-slate-300">Actions</h4>

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={approveNote}
              onChange={(e) => setApproveNote(e.target.value)}
              placeholder="Approval note (optional)"
              className="flex-1 bg-slate-800/60 border border-slate-700 text-slate-100 text-sm rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500/50"
            />
            <span title={approveDisabledReason ?? undefined}>
              <button
                onClick={() => approveM.mutate()}
                disabled={busy || approveDisabledReason != null}
                className="px-3 py-1.5 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 hover:bg-emerald-500/30 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {approveM.isPending ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                {approveM.isPending ? 'Approving…' : 'Approve'}
              </button>
            </span>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Rejection reason (required)"
              className="flex-1 bg-slate-800/60 border border-slate-700 text-slate-100 text-sm rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-red-500/50"
            />
            <span title={rejectReason.trim() ? undefined : 'Enter a reason to reject'}>
              <button
                onClick={() => rejectM.mutate()}
                disabled={busy || !rejectReason.trim()}
                className="px-3 py-1.5 rounded-md bg-red-500/20 border border-red-500/40 text-red-100 hover:bg-red-500/30 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {rejectM.isPending ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                {rejectM.isPending ? 'Rejecting…' : 'Reject'}
              </button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-slate-500 mb-1">{label}</dt>
      <dd className="text-slate-200">{children}</dd>
    </div>
  );
}

export default AiOrdersTab;
